import { compact, isObject, isString, find, findIndex, forEach, get, isEmpty, map, omit, set, sortBy } from 'lodash-es';

import uuidv4 from 'uuid/v4';
import { checkIsEmpty } from '../../utils';

/**
 * get question
 * @param generalData
 */
const getQuestionGroup = (generalData, questionsWithAnswers, covered) => {
  const questionWithAnswers = find(questionsWithAnswers, { code: generalData.code });
  const question = questionWithAnswers ? { ...questionWithAnswers } : { ...generalData };

  return question;
};

const handleQuestionGroup = (group, questionsWithAnswers, covered) => {
  let questions = [];

  // from web service
  if (!isEmpty(get(group, 'extra_data'))) {
    questions = map(get(group, 'extra_data'), generalData =>
      getQuestionGroup(generalData, questionsWithAnswers, covered)
    );
  }

  // from store
  if (!isEmpty(get(group, 'questions'))) {
    questions = [
      ...questions,
      ...map(get(group, 'questions'), question => getQuestionGroup(question, questionsWithAnswers, covered)),
    ];
  }

  return questions;
};

/**
 * get group
 * @param group
 */
const getGroup = (group, questions, covered) => ({
  ...group,
  id: !checkIsEmpty(get(group, 'id')) ? get(group, 'id') : uuidv4(),
  questions: handleQuestionGroup(group, questions, covered),
  // todo verify reel case
  groups: !isEmpty(get(group, 'groups'))
    ? map(get(group, 'groups'), groupItem => getGroup(groupItem, questions, covered))
    : undefined,
});

/**
 * get question
 * @param {*} question
 */
const getQuestion = (question, answerValue) => {
  try {
    let validation = [];

    if (!isEmpty(get(question, 'validation'))) {
      validation = get(question, 'validation');
    }

    if (question.required) {
      validation.push({ required: true, message: ' ' /* 'champ obligatoire' */ }); // HACK demo to not display error message
    }

    const result = {
      ...question,
      value: answerValue,
      validation,
    };
    return result;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`getQuestion ${e.stack}`);
  }

  return undefined;
};

const getPath = ({ objectFound, indexFinded, question, oldPath, name, findPath, covered }) => {
  const newFindPath = `${findPath}.find`;
  let newPath;

  if (oldPath) {
    newPath = `${oldPath}.${name}[${indexFinded}]`;
  } else {
    newPath = `${name}[${indexFinded}]`;
  }

  if (get(question, newFindPath)) {
    return getDeepPathFromAnswer({
      question,
      findObject: objectFound,
      findPath: newFindPath,
      oldPath: newPath,
      covered,
    });
  }

  return [newPath];
};

const replaceCondition = (condition, covered) => {
  const newCondition = { ...condition };

  for (let key in newCondition) {
    const value = newCondition[key];

    if (isObject(value)) {
      newCondition[key] = replaceCondition(value, covered);
    } else if (isString(value)) {
      if (value.indexOf('__') >= 0) {
        newCondition[key] = get(covered, value.replace(/__/g, ''), value);
      }
    }
  }

  return newCondition;
};

// get deep path from answer
const getDeepPathFromAnswer = ({ question, findObject, findPath = 'path', oldPath, covered }) => {
  const name = oldPath ? get(question, `${findPath}.name`) : get(question, `${findPath}.root`);
  const condition = get(question, `${findPath}.condition`);

  if (condition) {
    let conditionFilled = condition;

    if (condition.indexOf('__') >= 0) {
      conditionFilled = replaceCondition(JSON.parse(conditionFilled), covered);
    } else {
      conditionFilled = JSON.parse(conditionFilled);
    }

    const objectFound = find(get(findObject, name), conditionFilled);
    const indexFinded = findIndex(get(findObject, name), conditionFilled);

    return getPath({ objectFound, indexFinded, question, oldPath, name, findPath, covered }).flat();
  }

  return map(get(findObject, name), (obj, idx) =>
    getPath({ objectFound: obj, indexFinded: idx, question, oldPath, name, findPath, covered })
  ).flat();
};

const getCompletePaths = (question, answers, covered) => {
  const paths = getDeepPathFromAnswer({ question, findObject: answers, covered });

  // TODO better: Now it gets first answer if there's multiple path
  for (let i in paths) {
    const path = paths[i];
    return `${path}.${get(question, 'path.path')}`;
  }
};

/**
 * get answer value
 * @param {} answers
 * @param {} questionItem
 */
const getAnswerValue = (answers, questionItem, covered) => {
  if (!answers || isEmpty(answers)) {
    return;
  }

  if (get(questionItem, 'path.path')) {
    const path = getCompletePaths(questionItem, answers, covered);
    return get(answers, path);
  }

  return get(answers, questionItem.code);
};

/**
 * get part
 * @param part
 */
const getPart = (part, answers, covered) => {
  try {
    const questions = map(part?.questions, questionItem => {
      const answer = getAnswerValue(answers, questionItem, covered);
      const question = getQuestion(questionItem, answer);

      return question;
    });

    const groups = !isEmpty(part?.groups) ? map(part?.groups, group => getGroup(group, questions, covered)) : undefined;
    const result = { ...part, questions, groups };

    return result;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`getPart ${e.stack}`);
  }
  return undefined;
};

/**
 * get block loan
 * @param block
 */
const getBlock = ({ block, covered, answers = undefined, isSubscriber = false }) => {
  try {
    const parts = compact(
      map(block?.parts, partItem => {
        if (partItem.onlySubscriber && !isSubscriber) {
          return null;
        }

        const part = getPart(partItem, answers, covered);
        return part;
      })
    );

    const result = {
      ...omit(block, 'buttons', 'relations'),
      id: covered?.party?.ref || '__id__',
      displayTitle: block?.displayTitle || true,
      parts,
    };

    if (block?.titleNotSubscriber && !isSubscriber) {
      result.title = block?.titleNotSubscriber;
    }

    return result;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`getBlock ${e.stack}`);
  }
  return undefined;
};

/**
 *  get answers
 * @param {*} salesRoute
 */
const getAnswers = (salesRoute, quotationBlocks) => {
  try {
    const answers = {};

    forEach(quotationBlocks, block => {
      forEach(block?.parts, part => {
        forEach(part?.questions, question => {
          const pathRoot = question?.path?.root;

          if (pathRoot) {
            if (!get(answers, pathRoot)) {
              set(answers, pathRoot, get(salesRoute, pathRoot));
            }
          }
        });
      });
    });

    return answers;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }

  return undefined;
};

/**
 *
 * @param {*} param0
 */
const quotationBlockTransform = ({ salesRoute, relations }) => {
  try {
    const description = salesRoute?.questionnaires?.[0]?.description;
    const quotationBlocks = description?.quotation_blocks;
    const relationsDescription = description?.relations || [];

    if (quotationBlocks && quotationBlocks.standard) {
      const quotation = salesRoute?.quotations?.[0];
      const subscriberRef = quotation?.subscriber?.ref;
      const answers = getAnswers(salesRoute, quotationBlocks);

      const blocks = map(get(quotation, 'covereds', [{}]), covered => {
        const partie = find(salesRoute?.parties, { ref: covered.party.ref });
        const isSubscriber = covered?.party?.ref === subscriberRef;
        let emptyBaseBlock = quotationBlocks.standard;

        if (partie && partie.relations?.[0]?.type && quotationBlocks[partie.relations[0].type]) {
          emptyBaseBlock = quotationBlocks[partie.relations[0].type];
        }

        const aBlock = getBlock({ block: emptyBaseBlock, covered, answers, isSubscriber });

        return aBlock;
      });

      const generics = {};

      forEach(quotationBlocks, (block, typeBlock) => {
        generics[typeBlock] = getBlock({ block });
      });

      const availableRelations = sortBy(
        map(relationsDescription, relation => {
          const fullRelation = find(relations, { code: relation.code });

          if (fullRelation) {
            return { ...fullRelation, ...relation, value: relation.code };
          }

          return { ...relation, name: relation.name, value: relation.code };
        }),
        ['name']
      );

      return {
        quotations: blocks,
        generics,
        relations: availableRelations,
        stringsBlock: quotationBlocks.standard?.buttons,
      };
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`questionnaireBlockTransform: ${e.stack}`);
  }

  return {};
};

export default quotationBlockTransform;
